/*
  ForceGraph.jsx
*/

// Npm.
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Bezier } from 'bezier-js';

// Lib.
import escapeHtml from '../../utils/escapeHtml';
import getContrastColor from '../../utils/getContrastColor';
import { DEFAULT_NODE_COLOR } from '../../utils/constants';

// Hooks.
import useDimensions from '../../hooks/useDimensions';
import useDebounce from '../../hooks/useDebounce';
import useTheme from '../../hooks/useTheme';

// Makes.
import { makeStyles, cx } from '../../makes/makeStyles';

// Common.
import getVisibleGraphData from './getVisibleGraphData';

// Styles.
const useStyles = makeStyles({
  root: {
    overflow: 'hidden',  // Stops scroll wheel appearing when resizing.
    '& .force-graph-container .clickable': {
      cursor: 'inherit',  // Stops pointer when onBakgroundClick is supplied.
    },
  },
});

// Constants.
const INITIAL_ZOOM = 8;
const DEFAULT_MIN_ZOOM = 0.5;
const MAX_ZOOM = 20;
const CURVE_AMOUNT = 0.25;
const CIRCLE_SIZE = 0.25;  // TODO: Sync this up with "getNodeCircleSize" correctly.
const CIRCLE_STROKE_SIZE = 0.3;
const RECT_STROKE_SIZE = 0.2;
const RECT_ROUND = 0.5;
const RECT_X_PADDING = 0.4;
const RECT_Y_PADDING = 0.2;
const RECT_Y_ADJUSTMENT = 0.1;  // Rect adjustment for baseline "middle" text.
const RECT_ALPHA = 0.9;
const RECT_CIRCLE_SIZE = 4;
const RECT_FONT_MAX_SIZE = 4;

// Helper to get node size, if a circle.
function getNodeValue(node) {
  return Math.min(((node.collapsedCount || 0) + 1), 2) * CIRCLE_SIZE;
}

// Helper to get circle size.
function getNodeCircleSize(node) {
  const nodeValue = getNodeValue(node);
  if (nodeValue >= 0.5) {
    return 2.9; // TODO: Work out the algorithm for circle size with "nodeVal" prop.
  } else {
    return 2;
  }
}

// Helper to fit the graph into the viewbox.
function panToFit(fg, params, width, height) {

  const box = fg.getGraphBbox();
  if (!box) {
    return;
  }

  const [left, right] = box.x;
  const [top, bottom] = box.y;

  const topLeft = fg.screen2GraphCoords(0, 0, 0);
  const bottomRight = fg.screen2GraphCoords(width, height, 0);

  // Extra padding to apply to the pan to get the graph into view.
  // Is based on the current zoom.

  const panPaddingX = (bottomRight.x - topLeft.x) * 0.1;
  const panPaddingY = (bottomRight.y - topLeft.y) * 0.1;

  let dx = 0;
  let dy = 0;

  if (right < topLeft.x) {
    dx = (topLeft.x - right) + panPaddingX;
  } else if (left > bottomRight.x) {
    dx = (bottomRight.x - left) - panPaddingX;
  }

  if (bottom < topLeft.y) {
    dy = (topLeft.y - bottom) + panPaddingY;
  } else if (top > bottomRight.y) {
    dy = (bottomRight.y - top) - panPaddingY;
  }

  if (dx !== 0 || dy !== 0) {
    fg.centerAt(params.x - dx, params.y - dy, 100);
  }

}

// Helpers to get node properties.
function getNodeLabel(node) {
  return node?._botech?.label || 'untitled';
}
function getNodeColor(node) {
  return node?._botech?.color || DEFAULT_NODE_COLOR;
}
function getLinkColor(link) {
  return getNodeColor(link.source);
}

// Renders a circle label.
function nodeCanvasObjectForCircle(node, ctx, globalScale, theme, isSelected) {

  // Circle size. If applying stroke, reduce size to compensate.
  let circleSize = getNodeCircleSize(node);
  if (isSelected) {
    circleSize -= (CIRCLE_STROKE_SIZE * 0.5);
  }

  // Color properties.
  const color = getNodeColor(node);
  const backgroundColor = isSelected && !theme.light ? '#000' : color;
  const outlineColor = theme.light ? '#000' : color;

  // Draw circle.
  ctx.fillStyle = backgroundColor;
  ctx.beginPath();
  ctx.arc(node.x, node.y, circleSize, 0, 2 * Math.PI, false);
  ctx.fill();

  // Outlined when selected.
  if (isSelected) {
    ctx.strokeStyle = outlineColor;
    ctx.lineWidth = CIRCLE_STROKE_SIZE;
    ctx.stroke();
  }

  // If collapsed, draw a stroke and number of children.
  if (node.collapsedCount > 0) {

    // Get text size so we can exactly center the text.
    const label = String(node.collapsedCount);
    const fontSize = circleSize;
    ctx.font = `bold ${fontSize}px ${theme.font_family}`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const size = ctx.measureText(label);

    // Draw text in the middle.
    ctx.fillStyle = !theme.light && isSelected ? '#fff' : getContrastColor(backgroundColor);
    ctx.fillText(node.collapsedCount, node.x - (size.width * 0.5), node.y + (size.actualBoundingBoxAscent * 0.5));

  }
}

// Renders a text label.
function nodeCanvasObjectForText(node, ctx, globalScale, theme, isSelected) {

  // Measure node label.
  const label = getNodeLabel(node);
  const fontSize = Math.max(10 / globalScale, RECT_FONT_MAX_SIZE);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `${fontSize}px ${theme.font_family}`;
  const textWidth = ctx.measureText(label).width;

  // Measure additional collapsed count text on the start.
  let collapseText = '';
  let collapseWidth = 0;
  let collapsePad = 0;
  if (node.collapsedCount > 0) {
    collapseText = String(node.collapsedCount);
    collapseWidth = ctx.measureText(collapseText).width;
    collapsePad = fontSize * RECT_X_PADDING;
  }

  // Get text dimensions.
  const padX = fontSize * RECT_X_PADDING;
  const padY = fontSize * RECT_Y_PADDING;
  const offsetY = -fontSize * RECT_Y_ADJUSTMENT;  // Small correction for "textBaseline = middle".
  const width = textWidth + padX + collapseWidth + collapsePad;
  const height = fontSize + padY;
  const bgDims = [
    node.x - (width * 0.5),
    node.y - (height * 0.5) + offsetY,
    width,
    height,
  ];
  node.__bgDims = bgDims;  // to re-use in nodePointerAreaPaint.

  // Draw background rect.
  const color = getNodeColor(node);
  ctx.fillStyle = theme.light ? color : '#000';
  ctx.globalAlpha = RECT_ALPHA;
  ctx.beginPath();
  if (ctx.roundRect && RECT_ROUND > 0) {
    ctx.roundRect(...bgDims, RECT_ROUND);
  } else {
    ctx.rect(...bgDims);
  }
  ctx.fill();
  ctx.globalAlpha = 1;

  // Draw border if selected.
  if (isSelected) {
    ctx.strokeStyle = theme.light ? '#000' : color;
    ctx.lineWidth = RECT_STROKE_SIZE;
    ctx.stroke();
  }

  // Draw text.
  ctx.fillStyle = theme.light ? getContrastColor(color) : color;
  ctx.fillText(label, node.x + (collapseWidth * 0.5) + (collapsePad * 0.5), node.y);

  // Draw collapsed count.
  if (collapseText.length > 0) {

    // Get position.
    const xPos = node.x - (width * 0.5) + (collapseWidth * 0.5) + (padX * 0.5);
    const yPos = node.y;

    // Draw text.
    if (!theme.light) {
      ctx.fillStyle = '#fff';
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `bold ${fontSize}px ${theme.font_family}`;
    ctx.fillText(collapseText, xPos, yPos);

  }
}

// Renders a rectangle to detect clickable area.
function nodePointerAreaPaintForText(node, color, ctx) {
  const bgDims = node.__bgDims;
  if (bgDims) {
    ctx.fillStyle = color;
    ctx.fillRect(...bgDims);
  }
}

// Helper to get link line dash value.
function linkLineDash(link) {
  return link._botech?.trigger ? [1, 1] : false;
}

// Gets the link canvas object mode.
// XXX: Needs to be function, despite saying it allows string value as a prop.
function linkCanvasObjectMode() {
  return 'after';
}

// Renders a link according to this:
// https://github.com/ForecastHealth/botech-online-editor/issues/13
function linkCanvasObject(link, ctx, globalScale, isSelected, graphSettings) {

  if (!graphSettings.arrows) {
    return;
  }

  // Get link values with defaults.
  // NOTE: These need to reflect defaults located in link form.
  const botech = link._botech || {};
  const remove_value_from_source = botech.remove_value_from_source ?? true;
  const value_to_target_operator = botech.value_to_target_operator || 'multiply';

  // Work out what we want to draw.
  const drawMiddleArrow = !botech.set_by_surrogate;
  const drawMiddleCircle = !drawMiddleArrow;
  const drawStartLine = !remove_value_from_source;
  const drawEndMultiply = value_to_target_operator === 'multiply';

  if (
    drawMiddleArrow
    || drawMiddleCircle
    || drawStartLine
    || drawEndMultiply
  ) {

    const ARROW_WH_RATIO = 1.6;
    const ARROW_VLEN_RATIO = 0.2;
  
    // Arrow rendering based on:
    // https://github.com/vasturiano/force-graph/blob/master/src/canvas-force-graph.js

    const start = link.source;
    const end = link.target;
    if (!start || !end || !start.hasOwnProperty('x') || !end.hasOwnProperty('x')) {
      return;
    }

    const nodeRelSize = 4;
    const startR = Math.sqrt(Math.max(0, getNodeValue(start) || 1)) * nodeRelSize;
    const endR = Math.sqrt(Math.max(0, getNodeValue(end) || 1)) * nodeRelSize;

    const arrowColor = getLinkColor(link);

    // Construct bezier for curved lines.

    const bzLine = link.__controlPoints && new Bezier(start.x, start.y, ...link.__controlPoints, end.x, end.y);

    const getCoordsAlongLine = bzLine
      ? t => bzLine.get(t) // get position along bezier line
      : t => ({            // straight line: interpolate linearly
        x: start.x + (end.x - start.x) * t || 0,
        y: start.y + (end.y - start.y) * t || 0
      });

    const lineLen = bzLine
      ? bzLine.length()
      : Math.sqrt(Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2));

    // Arrow drawing.

    if (drawMiddleArrow) {
      const arrowLength = graphSettings.arrows > 1 ? (isSelected ? 3 : 2.5) : (isSelected ? 2.5 : 2);
      const arrowRelPos = 0.5;
      const arrowHalfWidth = arrowLength / ARROW_WH_RATIO / 2;
      const posAlongLine = startR + arrowLength + (lineLen - startR - endR - arrowLength) * arrowRelPos;
      const arrowHead = getCoordsAlongLine(posAlongLine / lineLen);
      const arrowTail = getCoordsAlongLine((posAlongLine - arrowLength) / lineLen);
      const arrowTailVertex = getCoordsAlongLine((posAlongLine - arrowLength * (1 - ARROW_VLEN_RATIO)) / lineLen);
      const arrowTailAngle = Math.atan2(arrowHead.y - arrowTail.y, arrowHead.x - arrowTail.x) - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(arrowHead.x, arrowHead.y);
      ctx.lineTo(arrowTail.x + arrowHalfWidth * Math.cos(arrowTailAngle), arrowTail.y + arrowHalfWidth * Math.sin(arrowTailAngle));
      ctx.lineTo(arrowTailVertex.x, arrowTailVertex.y);
      ctx.lineTo(arrowTail.x - arrowHalfWidth * Math.cos(arrowTailAngle), arrowTail.y - arrowHalfWidth * Math.sin(arrowTailAngle));
      ctx.fillStyle = arrowColor;
      ctx.fill();
    }

    // Circle drawing.

    if (drawMiddleCircle) {
      const width = graphSettings.arrows > 1 ? (isSelected ? 3 : 2) : (isSelected ? 2 : 1);
      const circleMid = getCoordsAlongLine(0.5);
      const circleRad = 1;
      ctx.beginPath();
      ctx.arc(circleMid.x, circleMid.y, circleRad, 0, 2 * Math.PI, false);
      ctx.strokeStyle = arrowColor;
      ctx.lineWidth = width / globalScale;
      ctx.stroke();
    }

    // Multiply symbol drawing.

    if (drawEndMultiply) {
      const width = graphSettings.arrows > 1 ? (isSelected ? 3 : 2) : (isSelected ? 2 : 1);
      const relPos = 0.75;
      const pos = graphSettings.nodeLabels > 1 ? relPos : Math.max(1 - ((endR / lineLen) * 2), relPos);
      const size = graphSettings.arrows > 1 ? 1 : 0.75;
      const symbolPos = getCoordsAlongLine(pos);
      const anglePos = getCoordsAlongLine(pos + 0.001);
      const angle = Math.atan2(symbolPos.y - anglePos.y, symbolPos.x - anglePos.x) - Math.PI / 2;
      const angleHalf = Math.PI * 0.25;
      ctx.beginPath();
      ctx.moveTo(symbolPos.x - (Math.cos(angle + angleHalf) * size), symbolPos.y - (Math.sin(angle + angleHalf) * size));
      ctx.lineTo(symbolPos.x + (Math.cos(angle + angleHalf) * size), symbolPos.y + (Math.sin(angle + angleHalf) * size));
      ctx.moveTo(symbolPos.x - (Math.cos(angle - angleHalf) * size), symbolPos.y - (Math.sin(angle - angleHalf) * size));
      ctx.lineTo(symbolPos.x + (Math.cos(angle - angleHalf) * size), symbolPos.y + (Math.sin(angle - angleHalf) * size));
      ctx.strokeStyle = arrowColor;
      ctx.lineWidth = width / globalScale;
      ctx.stroke();
    }

    // Starting line drawing.

    if (drawStartLine) {
      const width = graphSettings.arrows > 1 ? (isSelected ? 3 : 2) : (isSelected ? 2 : 1);
      const relPos = 0.25;
      const pos = graphSettings.nodeLabels > 1 ? relPos : Math.min((endR / lineLen) * 2, relPos);
      const size = graphSettings.arrows > 1 ? 1 : 0.75;
      const symbolPos = getCoordsAlongLine(pos);
      const anglePos = getCoordsAlongLine(pos + 0.001);
      const angle = Math.atan2(symbolPos.y - anglePos.y, symbolPos.x - anglePos.x) - Math.PI / 2;
      ctx.beginPath();
      ctx.moveTo(symbolPos.x - (Math.cos(angle) * size), symbolPos.y - (Math.sin(angle) * size));
      ctx.lineTo(symbolPos.x + (Math.cos(angle) * size), symbolPos.y + (Math.sin(angle) * size));
      ctx.strokeStyle = arrowColor;
      ctx.lineWidth = width / globalScale;
      ctx.stroke();
    }

  }
}

// Helper function to render a link label.
function getLinkLabelHtml(link, theme) {
  let label = '';
  if (link.source) {
    label += `<span style="color: ${escapeHtml(getNodeColor(link.source))};">⬤</span> ${escapeHtml(getNodeLabel(link.source))}`;
  }
  if (link.target) {
    if (label.length > 0) {
      label += ' → ';
    }
    label += `<span style="color: ${escapeHtml(getNodeColor(link.target))};">⬤</span> ${escapeHtml(getNodeLabel(link.target))}`;
  }
  return `<span style="font-family: ${escapeHtml(theme.font_family)};">${label}</span>`;
}

// Helper function to get a node label.
function getNodeLabelHtml(node, theme) {
  let html = `<span style="font-family: ${escapeHtml(theme.font_family)};"><span style="color: ${escapeHtml(getNodeColor(node))};">⬤</span> ${escapeHtml(getNodeLabel(node))}`;
  if (node.collapsedCount) {
    html += ` (${node.collapsedCount})`;
  }
  return html + '</span>';
}

// Helper to apply "curve" property to links inside the graphData.
function applyCurveToLinks(links) {

  // NOTE: The first render might contain strings for "source" and "target" as this
  // is also valid for force-graph. Ignore in this case.

  const groupedLinks = new Map();

  for (const link of links) {

    link.curve = 0;

    const sId = link.source?.id;
    const tId = link.target?.id;

    if (sId && tId) {
      const key = sId > tId ? `${sId}-${tId}` : `${tId}-${sId}`;
      groupedLinks.set(key, (groupedLinks.get(key) || []).concat(link));
    }
    
  }

  for (const arr of groupedLinks.values()) {
    if (arr.length > 0 && arr[0].source === arr[0].target) {

      // If this is a self-link, then add a minimum curve value.
      for (let i = 0; i < arr.length; i++) {
        const link = arr[i];
        link.curve = (i + 2) * CURVE_AMOUNT * 0.75;
      }

    } else if (arr.length > 1) {

      // Sort the arrows by source vs target, just to make it look nicer.
      arr.sort((a, b) => b.source.id > a.source.id ? 1 : (b.source.id < a.source.id ? -1 : 0));
      const mid = (arr.length - 1) / 2;

      for (let i = 0; i < arr.length; i++) {

        // If the target is in the other direction, flip the curve to correct it.
        const link = arr[i];
        const inverse = link.source.id > link.target.id ? -1 : 1;
        link.curve = (i - mid) * inverse * CURVE_AMOUNT;

      }
    }
  }
}

// Helper to get the zoom requires to fit everything inside, with 5% padding on either size.
// Based on "zoomToFit":
// https://github.com/vasturiano/force-graph/blob/master/src/force-graph.js
function getZoomToFit(width, height, minX, minY, maxX, maxY) {

  const absoluteMin = 1e-12;

  const zoom = Math.max(absoluteMin, Math.min(1e12,
    (width * 0.9) / (maxX - minX),
    (height * 0.9) / (maxY - minY)
  ));

  return zoom || absoluteMin;  // In case of NaN.

}

// Helper to get a good minimum zoom level. We don't want users to zoom really far out
// if their graph is small.
function getMinimumZoom(nodes, width, height) {

  if (nodes.length < 2) {
    return DEFAULT_MIN_ZOOM;
  }

  let minX = 0;
  let minY = 0;
  let maxX = 0;
  let maxY = 0;
  let first = true;

  for (const node of nodes) {
    if (first) {
      first = false;
      minX = node.x;
      minY = node.y;
      maxX = node.x;
      maxY = node.y;
    } else {
      minX = Math.min(node.x, minX);
      minY = Math.min(node.y, minY);
      maxX = Math.max(node.x, maxX);
      maxY = Math.max(node.y, maxY);
    }
  }

  return Math.min(DEFAULT_MIN_ZOOM, getZoomToFit(width, height, minX, minY, maxX, maxY));

}

/**
 * Renders the force graph across the screen.
 */
export default function ForceGraph({
  className,
  graphData,
  graphSettings,
  tagFilter,
  selectedNode,
  selectedLink,
  onLinkClick,
  onLinkRightClick,
  onNodeClick,
  onNodeRightClick,
  onBackgroundClick,
  onBackgroundRightClick,
  canvasRef,
  forceGraphRef,
}) {

  const theme = useTheme();
  const rootRef = useRef();
  const fgRef = useRef();

  // Get memoized set of nodes and links to hide, based on visibility settings.

  const useGraphData = useMemo(() => {
    return getVisibleGraphData(graphData.nodes, graphData.links, tagFilter);
  }, [graphData.nodes, graphData.links, tagFilter]);

  // Get memoized link curve settings.
  // NOTE: A bit naughty as we just mutate the links directly.
  // Force-graph mutates the objects itself as well. As long as we don't change "_botech".

  useMemo(() => {
    applyCurveToLinks(useGraphData.links);
  }, [useGraphData.links]);

  // NOTE: If the graph is going to always occupy the entire screen,
  // then just use the "useWindowSize" hook here.
  // TODO: Check how to stop flickering of the canvas when resizing without debounce.

  const { width, height } = useDebounce(useDimensions(rootRef), 0);

  // Get minimum zoom level.

  const minZoom = useMemo(() => {
    return getMinimumZoom(graphData.nodes, width, height);
  }, [graphData.nodes, width, height]);

  // Effect to zoom out and center once the initial scene loads.
  // NOTE: Once width and height are set, fgRef should be ready also.
  // TODO: Trigger this if graphData is completely new. Not sure how to detect this here though.

  const hasZoomed = useRef();

  useEffect(() => {
    if (!hasZoomed.current && width > 0 && height > 0 && fgRef.current) {
      hasZoomed.current = true;
      const box = fgRef.current.getGraphBbox();
      if (box) {

        const x = (box.x[0] + box.x[1]) * 0.5;
        const y = (box.y[0] + box.y[1]) * 0.5;
        fgRef.current.centerAt(x, y);

        const zoom = getZoomToFit(width, height, box.x[0], box.y[0], box.x[1], box.y[1]);
        fgRef.current.zoom(Math.min(zoom, INITIAL_ZOOM));

      }
    }
  }, [width, height]);

  // Effect to set force-graph ref prop, if one is provided.

  useEffect(() => {
    if (forceGraphRef) {
      forceGraphRef.current = fgRef.current;
      return () => {
        forceGraphRef.current = null;
      };
    }
  }, [forceGraphRef]);

  // Handlers.

  function handleEngineStop() {
    if (fgRef.current) {
      // TODO: Save data here, so if the user refreshes, the X and Y positions are the same?
    }
  }

  function handleZoomEnd(params) {
    if (fgRef.current && params.x !== 0 && params.y !== 0) {
      panToFit(fgRef.current, params, width, height);
    }
  }

  function handleRenderFramePost(ctx, globalScale) {
    if (canvasRef) {
      canvasRef.current = ctx.canvas;  // Sneakily set canvasRef here.
    }
  }

  function handleBackgroundRightClick(event) {
    if (onBackgroundRightClick) {

      // Gather up some useful properties based on the location of the right click
      // for use in the right-click menu. We do it here as we have access to the refs.
      const usefulProps = {};
      if (rootRef.current && fgRef.current) {
        const bbox = rootRef.current.getBoundingClientRect();
        usefulProps.coords = fgRef.current.screen2GraphCoords(event.clientX - bbox.left, event.clientY - bbox.top);
      }

      onBackgroundRightClick(event, usefulProps);

    }
  }

  function handleMouseDown(event) {
    // Check if a left mouse click, with CTRL held down.
    // This specific check will solve the below issue in "handleBackgroundClick".
    // TODO: Remove if using React 17 or above.
    let leftClick = false;
    if ('buttons' in event) {
      leftClick = event.buttons === 1;
    } else {
      leftClick = event.which === 1 || event.button === 1;
    }
    if (leftClick && event.ctrlKey) {
      handleBackgroundRightClick(event);
    }
  }

  function handleBackgroundClick(event) {
    if (event.ctrlKey) {
      // XXX: "ctrlKey" is false even when it's held down. This might be a react 16 thing,
      // but it is intead captured by the root div. The root div otherwise doesn't capture anything.
      handleBackgroundRightClick(event);
    } else if (onBackgroundClick) {
      onBackgroundClick(event);
    }
  }

  function handleDagError(loopNodeIds) {
    // We catch the DAG error here to allow the graph to be re-drawn
    // with new links, despite no longer being a DAG.
  }

  // Node label settings.

  const forceGraphProps = {
    nodeLabel: null,
  };

  if (graphSettings.nodeLabels === 2) {
    forceGraphProps.nodeCanvasObject = (node, ctx, globalScale) => nodeCanvasObjectForText(node, ctx, globalScale, theme, node === selectedNode);
    forceGraphProps.nodePointerAreaPaint = (node, color, ctx) => nodePointerAreaPaintForText(node, color, ctx);
  } else {
    forceGraphProps.nodeCanvasObject = (node, ctx, globalScale) => nodeCanvasObjectForCircle(node, ctx, globalScale, theme, node === selectedNode);
    forceGraphProps.nodeVal = getNodeValue;
    if (graphSettings.nodeLabels === 1) {
      forceGraphProps.nodeLabel = (node) => getNodeLabelHtml(node, theme);
    }
  }

  // Link label settings.

  if (graphSettings.linkLabels === 0) {
    forceGraphProps.linkLabel = null;
  } else if (graphSettings.linkLabels === 2) {
    // TODO.
  } else {
    forceGraphProps.linkLabel = (node) => getLinkLabelHtml(node, theme);
  }

  // Link arrow drawing function.

  const linkCanvasObjectFunc = useCallback((link, ctx, globalScale) => {
    return linkCanvasObject(link, ctx, globalScale, link === selectedLink, graphSettings);
  }, [linkCanvasObject, graphSettings, selectedLink]);

  // Physics settings.
  // TODO: Add forces to keep nodes in the bounding box so they don't
  // go trailing off into infinity.
  // https://github.com/vasturiano/react-force-graph/blob/master/example/collision-detection/index.html

  forceGraphProps.cooldownTicks = 0;
  forceGraphProps.d3VelocityDecay = 0.9;
  forceGraphProps.d3AlphaDecay = 0.02;
  forceGraphProps.d3AlphaMin = 0.01;
  forceGraphProps.dagMode = 'zout';

  if (graphSettings.physics === 1) {
    
    forceGraphProps.cooldownTicks = 100;

  } else if (graphSettings.physics === 2) {

    forceGraphProps.cooldownTicks = 200;
    forceGraphProps.d3VelocityDecay = 0.5;

  }

  // Effect to re-trigger the simulation when the physics setting changes.
  useEffect(() => {
    if (fgRef.current) {
      fgRef.current.d3ReheatSimulation();
    }
  }, [graphSettings.physics]);

  // UI callbacks. Mainly to adjust appearance when selecting nodes/links.
  // NOTE: Selected nodes will take priority, so if a node is selected then
  // don't use a selected link.
  const useSelectedLink = !selectedNode ? selectedLink : null;

  // TODO: Add this back in once i work out how to stop
  // it from re-triggering the physics engine and making things wobble.
  // const linkDirectionalParticles = useCallback((link) => {
  //   return link === useSelectedLink ? 4 : 0;
  // }, [useSelectedLink]);
  const linkDirectionalParticles = undefined;

  const linkWidth = useCallback((link) => {
    if (graphSettings.arrows > 1) {
      return link === useSelectedLink ? 3 : 2;
    } else {
      return link === useSelectedLink ? 2 : 1;
    }
  }, [useSelectedLink, graphSettings.arrows]);

  // Get class to use.

  const classes = useStyles();
  const cls = useMemo(() => cx('ForceGraph', classes.root, className), [classes.root, className]);
  
  return (
    <div
      className={cls}
      ref={rootRef}
      onMouseDown={handleMouseDown}
    >
      
      <ForceGraph2D
        ref={fgRef}
        width={width}
        height={height}
        graphData={useGraphData}
        nodeVal={CIRCLE_SIZE}
        zoom={INITIAL_ZOOM}
        nodeId="id"
        linkDirectionalParticleSpeed={0.005}
        linkDirectionalParticleWidth={graphSettings.arrows > 1 ? 4 : 3}
        linkDirectionalParticles={linkDirectionalParticles}
        linkDirectionalArrowLength={0}
        linkDirectionalArrowRelPos={0.5}
        linkWidth={linkWidth}
        linkLineDash={linkLineDash}
        linkCurvature="curve"
        linkCurveRotation="rot"
        nodeColor={getNodeColor}
        linkColor={getLinkColor}
        minZoom={minZoom}
        maxZoom={MAX_ZOOM}
        onEngineStop={handleEngineStop}
        onNodeClick={onNodeClick}
        onLinkClick={onLinkClick}
        onLinkRightClick={onLinkRightClick}
        onBackgroundClick={handleBackgroundClick}
        onBackgroundRightClick={handleBackgroundRightClick}
        onNodeRightClick={onNodeRightClick}
        onZoomEnd={handleZoomEnd}
        onRenderFramePost={handleRenderFramePost}
        onDagError={handleDagError}
        nodeCanvasObject={null}
        nodePointerAreaPaint={null}
        linkCanvasObjectMode={linkCanvasObjectMode}
        linkCanvasObject={linkCanvasObjectFunc}
        {...forceGraphProps}
      />

    </div>
  );
}
